import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {unminimizeCommentMutation as unminimizeCommentMutationQueryId} from '@github-ui/server-persisted-queries'
import type {UnminimizeCommentMutationMutation} from '@github-ui/server-persisted-queries/types/unminimizeCommentMutation'
import {type JSONRequestInit, reactFetchJSON} from '@github-ui/verified-fetch'
import type {Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import {beginCommentMinimizationOverlay, type OptimisticConnection} from './comment-connection-overlay'

function minimizationPath(owner?: string, repo?: string): string {
  if (owner && repo) {
    return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/comments/minimization?queryId=${unminimizeCommentMutationQueryId}`
  }

  const [, inferredOwner, inferredRepo] = window.location.pathname.split('/')
  if (!inferredOwner || !inferredRepo) {
    throw new Error('Failed to infer repository path for unminimizing comment')
  }

  return `/${encodeURIComponent(inferredOwner)}/${encodeURIComponent(inferredRepo)}/comments/minimization?queryId=${unminimizeCommentMutationQueryId}`
}

function writeRelayMinimizationState(
  store: RecordSourceProxy | RecordSourceSelectorProxy,
  id: string,
  fields: {
    isHidden: boolean
    minimizedReason: string | null
    pendingMinimizeReason: string | null
    isMinimized: boolean
  },
) {
  const comment = store.get(id)
  comment?.setValue(fields.isHidden, 'isHidden')
  comment?.setValue(fields.minimizedReason, 'minimizedReason')
  comment?.setValue(fields.pendingMinimizeReason, 'pendingMinimizeReason')
  comment?.setValue(fields.isMinimized, 'isMinimized')
}

export function unminimizeCommentMutation({
  environment,
  input: {id, owner, repo},
  onCompleted,
  onError,
  optimisticConnection,
}: {
  environment: Environment
  input: {id: string; owner?: string; repo?: string}
  onCompleted?: () => void
  onError?: (error?: Error) => void
  optimisticConnection?: OptimisticConnection
}) {
  const request: JSONRequestInit = {
    method: 'DELETE',
    body: {
      input: {subjectId: id},
    },
  }
  const path = minimizationPath(owner, repo)
  const optimisticFields = {
    isHidden: false,
    minimizedReason: null,
    pendingMinimizeReason: null,
  }
  const publication = publishOptimisticFields(id, optimisticFields)
  const overlay = optimisticConnection
    ? beginCommentMinimizationOverlay({connection: optimisticConnection, nodeId: id, fields: optimisticFields})
    : undefined
  const relayUpdate =
    typeof environment.applyUpdate === 'function'
      ? environment.applyUpdate({
          storeUpdater: store =>
            writeRelayMinimizationState(store, id, {
              isHidden: false,
              minimizedReason: null,
              pendingMinimizeReason: null,
              isMinimized: false,
            }),
        })
      : undefined
  const abortController = new AbortController()
  let settled = false
  const fail = (error: Error) => {
    if (settled) return
    settled = true
    relayUpdate?.dispose()
    publication.failed(error)
    overlay?.fail(error)
    onError?.(error)
  }

  void (async () => {
    try {
      const response = await reactFetchJSON(path, {...request, signal: abortController.signal})
      if (!response.ok) {
        throw new Error(`Failed to unminimize comment (${response.status})`)
      }
      const payload = (await response.json()) as {
        errors?: Array<{message?: string}>
        data?: UnminimizeCommentMutationMutation
      }
      const comment = payload.data?.unminimizeComment?.unminimizedComment
      if (!comment) {
        throw new Error(payload.errors?.[0]?.message ?? 'Failed to unminimize comment')
      }
      const isMinimized = comment.isMinimized
      const isHidden = isMinimized
      if (relayUpdate) {
        environment.commitUpdate(store => {
          writeRelayMinimizationState(store, id, {
            isHidden,
            minimizedReason: comment.minimizedReason ?? null,
            pendingMinimizeReason: comment.minimizedReason ?? null,
            isMinimized,
          })
        })
        relayUpdate.dispose()
      }
      const confirmedFields = {
        isHidden,
        minimizedReason: comment.minimizedReason ?? null,
        pendingMinimizeReason: comment.minimizedReason ?? null,
      }
      publication.completed(confirmedFields)
      overlay?.settle(confirmedFields)
      settled = true
      onCompleted?.()
    } catch (error) {
      if (settled) return
      const normalizedError =
        error instanceof Error
          ? error.name === 'AbortError'
            ? new Error('Unminimizing comment was cancelled')
            : error
          : new Error('Failed to unminimize comment')
      fail(normalizedError)
    }
  })()

  return {
    dispose: () => {
      abortController.abort()
      fail(new Error('Unminimizing comment was cancelled'))
    },
  }
}
